import React, { useState } from 'react';
import { 
  X, 
  Instagram, 
  Twitter, 
  Youtube, 
  MessageSquare,
  Users,
  Trophy,
  Map,
  Calendar,
  FileText,
  Book,
  Coins,
  Zap,
  Facebook,
  Send,
  Music,
  ChevronDown,
  Building2,
  User,
  Shield
} from 'lucide-react';
import { ThirdwebClient } from 'thirdweb';
import { useActiveAccount } from 'thirdweb/react';
import { isMasterAdminSync } from '../admin/constants';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navigate: (path: string) => void;
  isCurrentPath: (path: string) => boolean;
  client: ThirdwebClient;
}

const SOCIAL_LINKS = {
  instagram: 'https://www.instagram.com/scavenjer.game/',
  twitter: 'https://x.com/ScavenjerGame',
  youtube: 'https://www.youtube.com/@ScavenjerZaidek',
  discord: 'https://discord.com/invite/wX9hjx4cwA',
  facebook: 'https://www.facebook.com/scavenjergame',
  tiktok: 'https://www.tiktok.com/@scavenjergame',
  telegram: 'https://t.me/ScavenjerHub'
};

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, navigate, isCurrentPath }) => {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const account = useActiveAccount();
  const isAdmin = isMasterAdminSync(account?.address);

  if (!isOpen) return null;

  const handleNavClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] md:hidden" onClick={onClose}>
      <div 
        className="absolute top-0 right-0 h-full w-full bg-[#111111] p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-8">
            <img src="https://ik.imagekit.io/q9x52ygvo/Untitled.png?updatedAt=1731900408675" alt="Scavenjer Logo" className="w-12 h-12" />
          <button onClick={onClose} className="text-white">
            <X size={28} />
          </button>
        </div>
        
        <nav className="flex flex-col gap-4 text-lg">
          {/* User Profile Section (if connected) */}
          {account && (
            <>
              <button 
                onClick={() => handleNavClick('/profile')} 
                className={`nav-item flex items-center gap-3 ${isCurrentPath('/profile') ? 'active' : ''}`}
              >
                <User size={20} className="text-[#2DD4BF]" />
                <span>MY PROFILE</span>
              </button>
              
              {/* Admin Link (only for admin wallets) */}
              {isAdmin && (
                <button 
                  onClick={() => handleNavClick('/admin')} 
                  className={`nav-item flex items-center gap-3 ${isCurrentPath('/admin') ? 'active' : ''}`}
                >
                  <Shield size={20} className="text-orange-500" />
                  <span className="text-orange-500">ADMIN PORTAL</span>
                </button>
              )}
              
              <div className="border-t border-gray-800 -mx-6"></div>
            </>
          )}

          {/* Planned release: Simulations nav link preserved off-menu.
          <a 
            href="https://simulations-gamma.vercel.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item block text-center"
            onClick={onClose}
          >
            SIMULATIONS
          </a>
          
          <div className="border-t border-gray-800 -mx-6"></div>
          */}

          <button onClick={() => handleNavClick('/')} className={`nav-item ${isCurrentPath('/') ? 'active' : ''}`}>HOME</button>

          <button onClick={() => handleNavClick('/support-shop')} className={`nav-item ${isCurrentPath('/support-shop') ? 'active' : ''}`}>SUPPORT SHOP</button>

          <div className="border-t border-gray-800 -mx-6"></div>

          <div>
            <button onClick={() => toggleSection('more')} className="w-full flex justify-between items-center nav-item px-6">
                <span>MORE</span>
                <ChevronDown className={`transition-transform ${openSection === 'more' ? 'rotate-180' : ''}`} />
            </button>
            {openSection === 'more' && (
                <div className="flex flex-col gap-2 pl-4 mt-2">
                    <button onClick={() => handleNavClick('/hosts')} className="nav-item sub-item">Hosts</button>
                    <button onClick={() => handleNavClick('/leaderboard')} className="nav-item sub-item">Leaderboard</button>
                    <button onClick={() => handleNavClick('/careers')} className="nav-item sub-item">Careers</button>
                    <button onClick={() => handleNavClick('/events')} className="nav-item sub-item">Events</button>
                    <button onClick={() => handleNavClick('/roadmap')} className="nav-item sub-item">Roadmap</button>
                    <a href="https://scavenjerbusiness.com/" onClick={onClose} className="nav-item sub-item">For Businesses</a>

                    <button onClick={() => handleNavClick('/studios')} className="nav-item sub-item">Studios</button>
                    <button onClick={() => handleNavClick('/whitepaper')} className="nav-item sub-item">Whitepaper</button>
                    <button onClick={() => handleNavClick('/terms')} className="nav-item sub-item">Terms & Conditions</button>
                </div>
            )}
          </div>
        </nav>

        <div className="border-t border-gray-700 my-8" />

        <div className="flex justify-center gap-6">
          <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer"><Instagram/></a>
          <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer"><Twitter/></a>
          <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer"><Youtube/></a>
          <a href={SOCIAL_LINKS.discord} target="_blank" rel="noopener noreferrer"><MessageSquare/></a>
          <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer"><Facebook/></a>
          <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer"><Music/></a>
          <a href={SOCIAL_LINKS.telegram} target="_blank" rel="noopener noreferrer"><Send/></a>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
